import React, { useState } from 'react';
import './BusinessAnalystCourseBrochure.css';

import { useParams, useNavigate } from 'react-router-dom';



const BusinessAnalystBrochure = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const navigate = useNavigate();


  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Do I need technical experience for this course?",
      answer: "No technical experience is required. This course is designed for professionals from various backgrounds who want to transition into Salesforce business analysis roles."
    },
    {
      question: "Which certifications does this prepare me for?",
      answer: "The program covers material for Salesforce Business Analyst Certification and provides guidance for Administrator and Platform App Builder certifications."
    },
    {
      question: "What's the typical career path after completion?",
      answer: "Graduates typically become Salesforce Business Analysts, Requirements Analysts, or Process Consultants, with opportunities to grow into Solution Architect roles."
    }
  ];

  return (
    <div className="course-brochure">
      <header className="brochure-header">
        <h1>Salesforce Business Analyst Program</h1>
        <p className="subtitle">Master the art of bridging business needs with Salesforce solutions in 12 weeks</p>
        <div className="header-highlights">
          <div className="highlight-item">
            <span className="highlight-number">200+</span>
            <span className="highlight-label">Analysts Trained</span>
          </div>
          <div className="highlight-item">
            <span className="highlight-number">90%</span>
            <span className="highlight-label">Certification Pass Rate</span>
          </div>
          <div className="highlight-item">
            <span className="highlight-number">40+</span>
            <span className="highlight-label">Partner Companies</span>
          </div>
        </div>
      </header>

      <div className="brochure-content">
        <div className="tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'curriculum' ? 'active' : ''}`}
            onClick={() => setActiveTab('curriculum')}
          >
            Curriculum
          </button>
          <button 
            className={`tab-btn ${activeTab === 'outcomes' ? 'active' : ''}`}
            onClick={() => setActiveTab('outcomes')}
          >
            Career Outcomes
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <h2>Program Overview</h2>
              <p>Our Business Analyst program transforms you into a certified Salesforce professional who can effectively gather requirements, analyze processes, and design optimal Salesforce solutions.</p>
              
              <div className="why-choose">
                <h3>🚀 Why Choose Our Business Analyst Training? 🚀</h3>
                <ul>
                  <li><strong>Industry-Validated Curriculum:</strong> Designed by certified Salesforce Architects</li>
                  <li><strong>Real-World Simulations:</strong> Practice with actual business case studies</li>
                  <li><strong>End-to-End Process Training:</strong> From discovery to solution delivery</li>
                  <li><strong>No Technical Background Needed:</strong> We teach you the essential technical concepts</li>
                  <li><strong>Stakeholder Management:</strong> Master communication with both business and IT teams</li>
                </ul>
              </div>

              <div className="program-details">
                <div className="detail-card">
                  <h3>What You'll Learn</h3>
                  <ul>
                    <li>Requirements gathering techniques</li>
                    <li>Process mapping and optimization</li>
                    <li>User story creation and backlog management</li>
                    <li>Solution design documentation</li>
                    <li>UAT planning and execution</li>
                  </ul>
                </div>

                <div className="detail-card">
                  <h3>Program Features</h3>
                  <ul>
                    <li>Live case study workshops</li>
                    <li>JIRA and Agile methodology training</li>
                    <li>1:1 coaching from practicing BAs</li>
                    <li>Stakeholder management simulations</li>
                    <li>Certification exam preparation</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'curriculum' && (
            <div className="curriculum-content">
              <h2>12-Week Business Analyst Curriculum</h2>
              
              <div className="week-cards">
                <div className="week-card">
                  <h3>Week 1-2: Salesforce & Business Analysis Fundamentals</h3>
                  <ul>
                    <li>Business analyst role in Salesforce projects</li>
                    <li>Salesforce platform overview</li>
                    <li>Requirements gathering techniques</li>
                    <li>Stakeholder identification and management</li>
                  </ul>
                </div>

                <div className="week-card">
                  <h3>Week 3-4: Process Analysis & Documentation</h3>
                  <ul>
                    <li>As-is vs. To-be process mapping</li>
                    <li>BPMN and flowchart creation</li>
                    <li>Gap analysis techniques</li>
                    <li>User story writing and acceptance criteria</li>
                  </ul>
                </div>

                <div className="week-card">
                  <h3>Week 5-6: Solution Design & Configuration</h3>
                  <ul>
                    <li>Salesforce data model fundamentals</li>
                    <li>Security model and sharing settings</li>
                    <li>Declarative automation options</li>
                    <li>Solution design documentation</li>
                  </ul>
                </div>

                <div className="week-card">
                  <h3>Week 7-8: Agile & Project Management</h3>
                  <ul>
                    <li>Agile methodology for Salesforce projects</li>
                    <li>JIRA and backlog management</li>
                    <li>Sprint planning and ceremonies</li>
                    <li>Prioritization techniques (MoSCoW, ROI)</li>
                  </ul>
                </div>

                <div className="week-card">
                  <h3>Week 9-10: Testing & Deployment</h3>
                  <ul>
                    <li>Test planning and case development</li>
                    <li>UAT coordination and execution</li>
                    <li>Change management strategies</li>
                    <li>Training material development</li>
                  </ul>
                </div>

                <div className="week-card">
                  <h3>Week 11-12: Certification Prep & Capstone</h3>
                  <ul>
                    <li>Business Analyst certification domains</li>
                    <li>Practice exams and review</li>
                    <li>End-to-end capstone project</li>
                    <li>Career preparation and resume workshop</li>
                  </ul>
                </div>
              </div>

              <div className="tools-covered">
                <h3>🛠️ Tools & Methodologies Covered 🛠️</h3>
                <div className="tools-grid">
                  <div className="tool-category">
                    <h4>Analysis Tools</h4>
                    <ul>
                      <li>Lucidchart</li>
                      <li>Miro</li>
                      <li>Visio</li>
                    </ul>
                  </div>
                  <div className="tool-category">
                    <h4>Project Management</h4>
                    <ul>
                      <li>JIRA</li>
                      <li>Confluence</li>
                      <li>Agile methodologies</li>
                    </ul>
                  </div>
                  <div className="tool-category">
                    <h4>Salesforce Specific</h4>
                    <ul>
                      <li>Process Builder</li>
                      <li>Flow Designer</li>
                      <li>Salesforce Data Model</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'outcomes' && (
            <div className="outcomes-content">
              <h2>Career Outcomes</h2>
              
              <div className="outcome-stats">
                <div className="stat-card">
                  <h3>Average Salary Increase</h3>
                  <p className="stat-value">55%</p>
                  <p>for graduates transitioning to BA roles</p>
                </div>
                
                <div className="stat-card">
                  <h3>Certification Success</h3>
                  <p className="stat-value">88%</p>
                  <p>first-time pass rate for Business Analyst cert</p>
                </div>
              </div>

              <h3>Career Paths</h3>
              <ul className="job-roles">
                <li>Salesforce Business Analyst</li>
                <li>CRM Process Consultant</li>
                <li>Requirements Analyst</li>
                <li>Product Owner</li>
                <li>Solution Architect (with experience)</li>
              </ul>

              <div className="testimonial">
                <blockquote>
                  "This program helped me transition from operations to a Salesforce Business Analyst role at a consulting firm. Within a year, I was leading discovery sessions for Fortune 500 clients."
                </blockquote>
                <p className="testimonial-author">- Priya M., Senior Business Analyst at Cloud Solutions Inc.</p>
              </div>
            </div>
          )}
        </div>

        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`} key={index}>
                <button className="faq-question" onClick={() => toggleFaq(index)}>
                  {faq.question}
                  <span className="toggle-icon">{expandedFaq === index ? '-' : '+'}</span>
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <h2>Ready to Launch Your Salesforce Business Analyst Career?</h2>
          <p>Next cohort begins: August 5, 2025</p>
          <button onClick={() => navigate('/enroll')} className="enroll-btn">Enroll Now</button>
          <p className="payment-options">Flexible payment plans available including corporate sponsorship options</p>
        </div>
      </div>
    </div>
  );
};

export default BusinessAnalystBrochure;